import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Settings, Search, TrendingUp, LogOut, User as UserIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useAuth } from '../../../AuthContext';
import { topNavItems, getActiveTopNavId } from './navRegistry';

/**
 * TopNav - Global top navigation bar (52px, DESIGN-SPEC.md §3).
 *
 * `Logo · Overview · Holdings · Activity · Cash flow · Analytics · Insights ·
 * [⌘K command] · bell · settings-gear · avatar`
 *
 * Replaces the 256px AppSidebar as the primary navigation surface (issue #232).
 * Active item = `--accent-tint` bg + weight 600 (§3), text via `text-topbar-accent-text`
 * (bound to `--topbar-accent-text`, NOT `--accent-text`) — the topbar stays dark in both
 * console themes, so active text must always use the dark-theme accent value (#8fc4f7)
 * regardless of the active theme, or it fails WCAG AA in console-light (review fix,
 * see .claude/docs/issues/issue-232/sessions/2026-07-10_ui-design-expert_review.md #2).
 * Insights unread = 6px accent dot; Events unread = 7px red dot on the bell (§3/§4.7).
 *
 * The avatar button opens a small user menu (name/email + Logout) — this is
 * the rehomed location for AppSidebar's old UserSection/Logout button, since
 * TopNav has no footer area. Not in the DESIGN-SPEC mockup markup verbatim,
 * but required to avoid losing the logout affordance entirely (see session
 * notes for the full old-sidebar-feature -> new-home rehoming map).
 *
 * @param {Object} props
 * @param {number} props.insightsUnreadCount - drives the Insights nav dot
 * @param {number} props.eventsUnreadCount - drives the bell's red dot
 * @param {() => void} props.onOpenPalette - opens the ⌘K command palette
 * @param {boolean} props.mobileMenuOpen - controls the mobile overflow sheet
 * @param {(open: boolean) => void} props.onMobileMenuOpenChange
 */
export function TopNav({
  insightsUnreadCount = 0,
  eventsUnreadCount = 0,
  onOpenPalette,
  mobileMenuOpen = false,
  onMobileMenuOpenChange,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const activeId = getActiveTopNavId(location.pathname);
  const isSettingsActive = location.pathname === '/settings';
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const userMenuRef = React.useRef(null);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/.test(navigator.platform || navigator.userAgent);
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl K';

  // Close the user menu on outside click (no Radix Popover/DropdownMenu is
  // installed in this project yet — a minimal controlled panel avoids adding
  // a new dependency for a single menu with two items).
  React.useEffect(() => {
    if (!userMenuOpen) return undefined;
    const handleClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [userMenuOpen]);

  const initials = React.useMemo(() => {
    const name = user?.displayName || user?.email || '';
    const trimmed = name.trim();
    if (!trimmed) return 'U';
    return trimmed.charAt(0).toUpperCase();
  }, [user]);

  return (
    <header
      className="sticky top-0 z-40 flex h-[52px] shrink-0 items-center gap-2 border-b border-topbar-border bg-topbar px-4 font-sans text-[#e6eef5] md:gap-6 md:px-6"
      data-testid="topnav"
    >
      <Link
        to="/"
        className="flex shrink-0 items-center gap-2.5 rounded-md text-[#e6eef5] transition-colors duration-[120ms] ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
        aria-label="Portfolio Tracker home"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-[var(--accent,_#3b9eff)]">
          <TrendingUp className="h-3.5 w-3.5 text-[var(--on-accent,_#06121f)]" aria-hidden="true" />
        </span>
        <span className="hidden text-[13.5px] font-bold tracking-[0.01em] sm:inline">
          PORTFOLIO
        </span>
      </Link>

      {/* Desktop nav items (§3) */}
      <nav className="hidden items-center gap-1 text-[13px] md:flex" aria-label="Primary">
        {topNavItems.map((item) => {
          const isActive = item.id === activeId;
          const showDot = item.unread === 'insights' && insightsUnreadCount > 0 && !isActive;
          return (
            <Link
              key={item.id}
              to={item.defaultUrl}
              data-testid={`topnav-item-${item.id}`}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'inline-flex items-center rounded-md px-3 py-1.5 transition-colors duration-[120ms] ease-out',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]',
                isActive
                  ? 'bg-accent-tint font-semibold text-topbar-accent-text'
                  : 'text-[#7d94aa] hover:bg-white/5 hover:text-[#e6eef5]'
              )}
            >
              {item.label}
              {showDot && (
                <span
                  className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent,_#3b9eff)]"
                  aria-hidden="true"
                />
              )}
              {showDot && <span className="sr-only"> (unread insights)</span>}
            </Link>
          );
        })}
      </nav>

      <div className="ml-auto flex shrink-0 items-center gap-2 md:gap-3.5">
        {/* ⌘K command trigger */}
        <button
          type="button"
          onClick={onOpenPalette}
          data-testid="topnav-command-trigger"
          aria-label="Open command palette"
          className={cn(
            'hidden h-[30px] w-60 items-center gap-2 rounded-md bg-white/[0.06] px-2.5 text-[12px] text-[#7d94aa] sm:flex',
            'transition-colors duration-[120ms] ease-out hover:text-[#e6eef5]',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]'
          )}
        >
          <Search className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">Command… buy, sell, jump to</span>
          <span className="ml-auto shrink-0 rounded border border-[#2a4763] px-1 font-mono text-[10px]">
            {shortcutLabel}
          </span>
        </button>

        {/* Mobile: icon-only search trigger */}
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Open command palette"
          className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-[#7d94aa] transition-colors duration-[120ms] ease-out hover:text-[#e6eef5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)] sm:hidden"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </button>

        <Link
          to="/events"
          aria-label={eventsUnreadCount > 0 ? `Events (${eventsUnreadCount} unread)` : 'Events'}
          title="Events"
          data-testid="topnav-events-bell"
          className="relative flex h-[30px] w-[30px] items-center justify-center rounded-md text-[#7d94aa] transition-colors duration-[120ms] ease-out hover:text-[#e6eef5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {eventsUnreadCount > 0 && (
            <span
              className="absolute right-1 top-1 h-[7px] w-[7px] rounded-full border-[1.5px] border-topbar bg-[#e5484d]"
              aria-hidden="true"
            />
          )}
        </Link>

        <Link
          to="/settings"
          aria-label="Settings"
          title="Settings"
          aria-current={isSettingsActive ? 'page' : undefined}
          data-testid="topnav-settings-gear"
          className={cn(
            'flex h-[30px] w-[30px] items-center justify-center rounded-md transition-colors duration-[120ms] ease-out',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]',
            isSettingsActive ? 'text-topbar-accent-text' : 'text-[#7d94aa] hover:text-[#e6eef5]'
          )}
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
        </Link>

        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((open) => !open)}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={user?.displayName || user?.email}
            data-testid="topnav-avatar"
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#16304a] text-[11px] font-semibold text-topbar-accent-text transition-colors duration-[120ms] ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
          >
            {user?.photo ? (
              <img src={user.photo} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              initials
            )}
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              aria-label="Account"
              data-testid="topnav-user-menu"
              className="absolute right-0 top-[36px] z-40 w-52 rounded-md border border-border bg-popover p-1 text-foreground shadow-lg"
            >
              <div className="flex items-center gap-2 border-b border-hairline px-2.5 py-2 text-[12.5px]">
                <UserIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{user?.displayName || user?.email}</span>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex min-h-[36px] w-full items-center gap-2 rounded px-2.5 text-left text-[13px] transition-colors duration-[120ms] ease-out hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
              >
                <Settings className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Settings
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
                className="flex min-h-[36px] w-full items-center gap-2 rounded px-2.5 text-left text-[13px] text-[#f0736f] transition-colors duration-[120ms] ease-out hover:bg-[rgba(229,72,77,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
              >
                <LogOut className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Log out
              </button>
            </div>
          )}
        </div>

        {/* Mobile overflow menu trigger */}
        <button
          type="button"
          onClick={() => onMobileMenuOpenChange?.(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileMenuOpen}
          data-testid="topnav-mobile-trigger"
          className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-[#7d94aa] transition-colors duration-[120ms] ease-out hover:text-[#e6eef5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)] md:hidden"
        >
          <MobileMenuIcon open={mobileMenuOpen} />
        </button>
      </div>

      {mobileMenuOpen && (
        <MobileNavSheet
          activeId={activeId}
          insightsUnreadCount={insightsUnreadCount}
          onNavigate={() => onMobileMenuOpenChange?.(false)}
        />
      )}
    </header>
  );
}

function MobileMenuIcon({ open }) {
  if (open) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/**
 * MobileNavSheet - condensed top-bar overflow menu for small viewports.
 * The old AppSidebar had a mobile sheet (via SidebarProvider's isMobile state);
 * TopNav is a fixed 52px bar with no room for full horizontal nav below ~768px,
 * so this dropdown panel preserves the same set of destinations on mobile.
 */
function MobileNavSheet({ activeId, insightsUnreadCount, onNavigate }) {
  return (
    <div
      role="menu"
      aria-label="Navigation"
      data-testid="topnav-mobile-sheet"
      className="absolute left-0 right-0 top-[52px] z-40 flex flex-col gap-0.5 border-b border-topbar-border bg-topbar p-2 shadow-lg md:hidden"
    >
      {topNavItems.map((item) => {
        const isActive = item.id === activeId;
        const showDot = item.unread === 'insights' && insightsUnreadCount > 0 && !isActive;
        return (
          <Link
            key={item.id}
            to={item.defaultUrl}
            role="menuitem"
            onClick={onNavigate}
            className={cn(
              'flex min-h-[44px] items-center rounded-md px-3 text-[13px] transition-colors duration-[120ms] ease-out',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]',
              isActive
                ? 'bg-accent-tint font-semibold text-topbar-accent-text'
                : 'text-[#7d94aa] hover:bg-white/5 hover:text-[#e6eef5]'
            )}
          >
            {item.label}
            {showDot && (
              <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent,_#3b9eff)]" aria-hidden="true" />
            )}
          </Link>
        );
      })}
    </div>
  );
}

export default TopNav;
